// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { AccountCreationFormContext } from "@store/Library/Widgets/AccountCreation/AccountCreationForm";
import React from "react";
import { css } from "styled-components";
import { connect, ResolveThunks } from "react-redux";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@store/Framework/Store/Store/Components/AccountCreationForm/Handlers/SetFieldValue";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import Loader from "@store/Library/Components/Toolkit/Loader";
import debounce from "lodash/debounce";
import constants from "@store/constants";
import TextSeparator from "@store/Library/Components/Toolkit/TextSeparator";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import Radio from "@insite/mobius/Radio";
import RadioGroup from "@insite/mobius/RadioGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";

interface OwnProps {
    extendedStyles?: AccountCreationFormFieldStyles;
}

const mapStateToProps = (state: ApplicationState) => {
    const { userName, email } = state.context.session;
    const currentUserIsGuest = getCurrentUserIsGuest(state);
    return {
        fieldValues: state.store.components.accountCreationForm.fieldValues,
        currentCountry: state.context.session.currency,
        isSignedIn: userName && !currentUserIsGuest,
        email,
    };
};

const mapDispatchToProps = {
    setFieldValue,
    loadCurrentCountries,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface AccountCreationFormFieldStyles {
    form?: InjectableCss;
    container?: GridContainerProps;
    thirdsGridItem?: GridItemProps;
    halfGridItem?: GridItemProps;
    fullGridItem?: GridItemProps;
    textFieldText?: TextFieldPresentationProps;
    textAreaText?: TextAreaProps;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
    sectionTitle?: TypographyPresentationProps;
}

export const accountCreationFormFieldStyles: AccountCreationFormFieldStyles = {
    container: {
        css: css`
            form {
                width: 100%;
            }
        `,
    },
    thirdsGridItem: { width: [12, 12, 4, 4, 4] },
    halfGridItem: { width: [12, 12, 6, 6, 6] },
    fullGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            width: 100%;
        `,
    },
    sectionTitle: {
        variant: "h4",
        forwardAs: "h2",
    },
};

const fieldConfig: Record<
    string,
    {
        label: string;
        requiredParentFieldKey?: string; // parent field needs a value to be valdiated
        requiredParentFieldValue?: string; // parent field needs a value to be valdiated
        placeholder: string;
        required: boolean;
        regex?: RegExp;
        regexErrorMessage?: string;
    }
> = constants.accountCreationFormFields;

const AccountCreationFormFields = ({ setFieldValue, fieldValues, isSignedIn, extendedStyles }: Props) => {
    const [styles] = React.useState(() => mergeToNew(accountCreationFormFieldStyles, extendedStyles));
    const [localFieldValues, setLocalFieldValues] = React.useState<Record<string, string>>({}); // Mirrors actual values, to be used before debounce happen
    const debounceMapRef = React.useRef<Record<string, any>>({});
    const accountCreationFormContext = React.useContext(AccountCreationFormContext);
    const [errorMessages, setErrorMessages] = React.useState<Record<string, string>>({});

    React.useEffect(() => {
        setFieldValue({ key: "businessOrIndividual", value: "individual" });
        setLocalFieldValues(prev => ({ ...prev, ["businessOrIndividual"]: "individual" }));
    }, []);

    React.useEffect(() => {
        Object.entries(fieldConfig).forEach(([key, config]) => {
            const value = fieldValues[key];
            const emptyTheValue = () => {
                setFieldValue({ key, value: "" });
                setFieldError(key, "");
                setLocalFieldValues(prev => ({ ...prev, [key]: "" }));
            };
            // Always validated
            if (config.requiredParentFieldKey) {
                if (fieldValues[config.requiredParentFieldKey] !== config.requiredParentFieldValue) {
                    if (value) {
                        emptyTheValue();
                    }
                    if (accountCreationFormContext.validators[key]) {
                        delete accountCreationFormContext.validators[key];
                    }
                    return; // Do Not Validate Field
                }
                if (!fieldValues[config.requiredParentFieldKey]) {
                    if (value) {
                        emptyTheValue();
                    }
                    if (accountCreationFormContext.validators[key]) {
                        delete accountCreationFormContext.validators[key];
                    }
                    return; // Do Not Validate Field
                }
            }

            const validator = () => validateInputField(key, value, config.label);
            accountCreationFormContext.validators[key] = validator;

            if (value !== undefined) {
                validator();
            }
        });
    }, [fieldValues]);

    const requiredTextFieldChangeHandler = (
        key: string,
        event:
            | React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
            | React.ChangeEvent<HTMLInputElement>
            | React.FormEvent<HTMLInputElement | HTMLSelectElement>,
    ) => {
        const target = event.target as HTMLInputElement | HTMLSelectElement;
        const value = target.value;
        setLocalFieldValues(prev => ({ ...prev, [key]: value })); // Display only, to hide lag from debounce

        // Save individual debounces to play well with Chrome's super fast autofill
        if (!debounceMapRef.current[key]) {
            debounceMapRef.current[key] = debounce((key: string, value: string) => {
                setFieldValue({ key, value });
            }, 200);
        }
        debounceMapRef.current[key](key, value);

        setTimeout(() => {
            debounceMapRef.current[key].flush(); // Calls the last one always, for Chrome's autofill
        }, 250);
    };

    const requiredTextFieldBlurHandler = (
        key: string,
        event:
            | React.ChangeEvent<HTMLInputElement>
            | React.ChangeEvent<HTMLSelectElement>
            | React.ChangeEvent<HTMLTextAreaElement>,
    ) => {
        const value = event.target.value;
        setLocalFieldValues(prev => ({ ...prev, [key]: value }));
        setFieldValue({ key, value });
    };

    const setFieldError = (field: string, message: string) => {
        setErrorMessages(prev => ({ ...prev, [field]: message }));
    };

    const validateInputField = (fieldKey: string, value: string | undefined, label: string): boolean => {
        let errorMessage = "";
        if (fieldConfig[fieldKey].required) {
            errorMessage = !value?.trim() ? (siteMessage("Field_Required", translate(label)) as string) : "";
        }

        // Check for a regex
        if (!errorMessage && fieldConfig[fieldKey].regex instanceof RegExp && value) {
            if (!fieldConfig[fieldKey].regex.test(value)) {
                errorMessage = fieldConfig[fieldKey].regexErrorMessage
                    ? fieldConfig[fieldKey].regexErrorMessage
                    : (siteMessage("Field_Invalid_Format", translate(label)) as string);
            }
        }

        setFieldError(fieldKey, errorMessage);
        return !errorMessage;
    };

    if (isSignedIn) {
        return null;
    }

    if (!localFieldValues.businessOrIndividual) {
        return <Loader />;
    }

    return (
        <GridContainer {...styles.container} data-test-selector="accountCreationFormFields">
            <TextSeparator />
            <GridItem {...styles.fullGridItem}>
                <RadioGroup
                    value={localFieldValues.businessOrIndividual}
                    horizontal
                    onChangeHandler={e => requiredTextFieldChangeHandler("businessOrIndividual", e)}
                >
                    <Radio value="business">{translate("Business")}</Radio>
                    <Radio value="individual">{translate("Individual")}</Radio>
                </RadioGroup>
            </GridItem>
            <GridItem {...styles.halfGridItem}>
                <TextField
                    {...styles.textFieldText}
                    label={translate(fieldConfig.legalName.label)}
                    disabled={localFieldValues.businessOrIndividual !== "business"}
                    required={localFieldValues.businessOrIndividual === "business"}
                    placeholder={translate(fieldConfig.legalName.placeholder)}
                    value={localFieldValues.legalName || ""}
                    error={errorMessages.legalName}
                    maxLength={100}
                    onBlur={e => requiredTextFieldBlurHandler("legalName", e)}
                    onChange={e => requiredTextFieldChangeHandler("legalName", e)}
                />
            </GridItem>

            <GridItem {...styles.halfGridItem}>
                <TextField
                    {...styles.textFieldText}
                    label={translate(fieldConfig.doingBusinessAs.label)}
                    disabled={localFieldValues.businessOrIndividual !== "business"}
                    required={localFieldValues.businessOrIndividual === "business"}
                    placeholder={translate(fieldConfig.doingBusinessAs.placeholder)}
                    value={localFieldValues.doingBusinessAs || ""}
                    error={errorMessages.doingBusinessAs}
                    maxLength={100}
                    onBlur={e => requiredTextFieldBlurHandler("doingBusinessAs", e)}
                    onChange={e => requiredTextFieldChangeHandler("doingBusinessAs", e)}
                />
            </GridItem>

            <GridItem {...styles.halfGridItem}>
                <TextField
                    {...styles.textFieldText}
                    label={translate(fieldConfig.individualName.label)}
                    required={fieldConfig.individualName.required}
                    placeholder={translate(fieldConfig.individualName.placeholder)}
                    value={localFieldValues.individualName || ""}
                    error={errorMessages.individualName}
                    maxLength={100}
                    onBlur={e => requiredTextFieldBlurHandler("individualName", e)}
                    onChange={e => requiredTextFieldChangeHandler("individualName", e)}
                />
            </GridItem>

            <TextSeparator />
            <GridItem {...styles.fullGridItem}>
                <Typography {...styles.sectionTitle}>{translate("Contact Information")}:</Typography>
            </GridItem>

            <GridItem {...styles.halfGridItem}>
                <TextField
                    {...styles.textFieldText}
                    label={translate(fieldConfig.phone.label)}
                    required={fieldConfig.phone.required}
                    placeholder={translate(fieldConfig.phone.placeholder)}
                    value={localFieldValues.phone || ""}
                    error={errorMessages.phone}
                    maxLength={20}
                    onBlur={e => requiredTextFieldBlurHandler("phone", e)}
                    onChange={e => requiredTextFieldChangeHandler("phone", e)}
                />
            </GridItem>

            <GridItem {...styles.halfGridItem}>
                <TextField
                    {...styles.textFieldText}
                    label={translate(fieldConfig.email.label)}
                    required={fieldConfig.email.required}
                    placeholder={translate(fieldConfig.email.placeholder)}
                    value={localFieldValues.email || ""}
                    error={errorMessages.email}
                    maxLength={100}
                    onBlur={e => requiredTextFieldBlurHandler("email", e)}
                    onChange={e => requiredTextFieldChangeHandler("email", e)}
                />
            </GridItem>

            <GridItem {...styles.fullGridItem}>
                <TextArea
                    {...styles.textAreaText}
                    label={translate(fieldConfig.additionalInformation.label)}
                    required={fieldConfig.additionalInformation.required}
                    placeholder={translate(fieldConfig.additionalInformation.placeholder)}
                    value={localFieldValues.additionalInformation || ""}
                    error={errorMessages.additionalInformation}
                    maxLength={500}
                    onBlur={e => requiredTextFieldBlurHandler("additionalInformation", e)}
                    onChange={e => requiredTextFieldChangeHandler("additionalInformation", e)}
                />
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(AccountCreationFormFields);
